/**
 * Dialog Size Registry
 *
 * Standard dialog sizes for consistent UI. All dialogs MUST use one of these sizes.
 *
 * Usage:
 *   import { DialogSize } from '@/components/templates';
 *   <StyledDialog size="standard" ...>
 *
 * Adding a new size:
 *   1. Document the use case (when would this size be needed?)
 *   2. Add to this registry with a semantic name
 *   3. Then use it in your dialog
 *
 * DO NOT use arbitrary max-width values in dialogs.
 */

/**
 * Dialog size presets.
 *
 * | Size | Max Width | Typical Use Case |
 * |------|-----------|------------------|
 * | compact | 444px | Simple confirmations, quick acknowledgments |
 * | standard | 600px | Most dialogs (DEFAULT) |
 * | form | 900px | Multi-field forms, upload dialogs |
 * | large | 1200px | Complex forms, data tables |
 * | fullWidth | 1536px | Media viewers, lightboxes |
 */
export const dialogMaxWidths = {
  /**
   * Compact dialogs for simple confirmations.
   * Use for: Yes/No prompts, quick acknowledgments, minimal content.
   * ~444px width
   */
  compact: '444px',

  /**
   * Standard dialog size (DEFAULT).
   * Use for: Most dialogs, single forms, info dialogs, delete confirmations.
   * ~600px width
   */
  standard: '600px',

  /**
   * Form dialogs with multiple fields.
   * Use for: Multi-section forms, upload dialogs, wizards.
   * ~900px width
   */
  form: '900px',

  /**
   * Large content dialogs.
   * Use for: Complex forms, data tables, side-by-side comparisons.
   * ~1200px width
   */
  large: '1200px',

  /**
   * Full-width dialogs for media.
   * Use for: Image lightboxes, video players, document viewers.
   * ~1536px width
   */
  fullWidth: '1536px',
} as const;

export type DialogSize = keyof typeof dialogMaxWidths;

/**
 * @deprecated Use dialogMaxWidths[size] directly.
 * Kept for backward compatibility — callers using dialogSizes.X should migrate to the size prop.
 */
export const dialogSizes = dialogMaxWidths;
